import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface Props {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export const ConfirmDeleteDialog = ({
  open,
  title,
  description,
  confirmLabel = "Retirer",
  cancelLabel = "Annuler",
  onCancel,
  onConfirm,
}: Props): JSX.Element | null => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => confirmRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-title"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
    >
      <button
        type="button"
        aria-label="Fermer"
        onClick={onCancel}
        className="absolute inset-0 bg-ink/60 animate-fade-in"
      />

      <div className="relative w-full max-w-sm bg-bg rounded-modal shadow-warm-lg p-6 animate-scale-in">
        <h2
          id="confirm-delete-title"
          className="display-font text-xl font-semibold text-ink leading-tight text-center"
        >
          {title}
        </h2>

        {description && (
          <p className="mt-3 text-sm text-ink-soft leading-snug text-center">
            {description}
          </p>
        )}

        <div className="mt-6 flex flex-col-reverse sm:flex-row gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-surface border border-border text-ink rounded-button py-3 px-5 font-medium transition-colors hover:bg-bg"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className="flex-1 bg-danger text-white rounded-button py-3 px-5 font-semibold shadow-warm transition-opacity hover:opacity-90"
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
